<template>
	<div class="content-page content-page--customers">
		<div ref="customerContentPageHeading" class="content-page__heading">
			<h2 class="content-page__title">Clients</h2>
		</div>
		<div
			class="
				results-toolbar results-toolbar--variant-two"
			ref="customerResultToolbar"
		>
			<SearchOptions
				v-model="searchOptions"
				@change="onSearchChange"
				@autosuggest-selection="onSearchOptionsSelected"
				@autosuggest-hidden="autosuggestVisible = false"
				@autosuggest-visible="autosuggestVisible = true"
				:autosuggestHideGroupsLabel="searchOptions.searchType !== 'all'"
				searchTypeDefaultValue="all"
				:searchTypeOptions="searchTypeOptions"
				:autosuggestOptions="autosuggestOptions"
				:timeout="0"
				autosuggest
				ref="searchWithAutosuggestRef"
				:width="'444px'"
			/>
			<DropdownSort
				:options="ddsOptions"
				:showClearSelectedButton="false"
				v-model="ddsValue"
			/>

			<Toggle
				:icon="'icon-favorite-select'"
				v-model="currentFavorite"
				v-log="{
					event: 'change',
					target: 'Clients Favorites Sort'
				}"
				@change="updateFavoriteOnVuex"
			/>
			<div class="results-toolbar__group">
				<Button
					v-if="hasResults && hasOpenAccordions"
					:btnClick="collapseAllTiers"
					labelText="Collapse All Tiers"
					class="results-toolbar__collapse"
					className="btn--link"
				/>
				<ResultCounter v-if="hasResults" :counter="resultCounter" />
			</div>
		</div>
		<ScrollContainer
			v-if="hasResults"
			variant="infinity-scroll"
			:hasMoreItems="hasMoreItemsToLoad"
			@scrollEnd="handleScrollEnd"
			:overlay="autosuggestVisible"
		>
			<CustomersList :items="customers.items" ref="customersList" />
		</ScrollContainer>
		<Spinner
			v-if="loading || loadingEnvironments"
			:processor="{ loading: true }"
			data-test="spinner"
		/>
		<NoResults
			v-if="!loading && !loadingEnvironments && !hasResults"
			data-test="noResults"
		/>
	</div>
</template>

<script>
import DropdownSort from '@/components/forms/dropdown/dropdown-sort';
import SearchOptions from '@/components/forms/search/search-options.vue';
import ResultCounter from '@/site-components/result-counter/result-counter';
import Toggle from '@/components/forms/selection-control/toggle/toggle';
import Button from '@/components/forms/button/button';
import Spinner from '@/components/visual/spinner/spinner';
import CustomersList from './customers-list';
import NoResults from '@/site-components/no-results/no-results';
import ScrollContainer from '@/components/shells/scroll-container/scroll-container';

// mixins
//;
import { LoggingMixin } from 'vue-ui-layout';

// vuex
import { mapActions, mapGetters, mapState } from 'vuex';

// libraries
import { getDifferentValues } from '@/utils/function';
import _ from 'lodash';

export default {
	name: 'Clients',
	mixins: [LoggingMixin],
	components: {
		DropdownSort,
		ResultCounter,
		SearchOptions,
		Toggle,
		Button,
		Spinner,
		CustomersList,
		ScrollContainer,
		NoResults
	},
	props: {
		search: String,
		searchType: String,
		sortBy: String,
		sortDirection: String,
		favoritesort: Boolean
	},
	data() {
		return {
			filters: {
				searchType: this.searchType || 'all',
				sortby: this.sortBy,
				sortdirection: this.sortDirection,
				favoritesort: this.currentFavorite
			},
			page: 1,
			defaultFilters: {
				search: '',
				searchType: 'all',
				sortby: 'name',
				sortdirection: 'asc',
				favoritesort: true
			},
			searchOptions: {
				search: '',
				searchType: 'all'
			},
			ddsOptions: [
				{ text: 'Name', value: 'name' },
				{ text: 'Number', value: 'number' }
			],
			searchTypeOptions: [
				{
					text: 'All',
					value: 'all',
					placeholder:
						'Search by client name/#, company, or sub location'
				},
				{
					text: 'Client',
					value: 'customer',
					placeholder: 'Search by client name/#'
				},
				{
					text: 'Company',
					value: 'company',
					placeholder: 'Search by company name'
				},
				{
					text: 'Sub Location',
					value: 'location',
					placeholder: 'Search by sub location name'
				}
			],
			autosuggestOptions: [],
			loading: true,
			loadingMore: false,
			loadingEnvironments: true,
			autosuggestVisible: false
		};
	},
	async created() {
		this.loadingEnvironments = true;
		await this.setOptionsEnvironments({ active: true });
		this.loadingEnvironments = false;
	},
	beforeRouteEnter(to, from, next) {
		if (to.name === 'clients-summary') {
			to.meta.breadcrumb = [];
		}
		next();
	},
	computed: {
		...mapState('customers', []),
		...mapState('customers', [
			'customers',
			'customersAutosuggestions',
			'customersAccordionStatuses',
			'companiesAccordionStatuses'
		]),
		...mapGetters('customers', ['favoriteToggle']),
		ddsValue: {
			get() {
				return {
					sortby: this.filters.sortby,
					sortdirection: this.filters.sortdirection
				};
			},
			set(value) {
				this.filters.sortby = value.sortby;
				this.filters.sortdirection = value.sortdirection;
			}
		},
		currentFavorite: {
			get() {
				return this.filters.favoritesort;
			},
			set(value) {
				this.filters.favoritesort = JSON.parse(value);
			}
		},
		hasResults() {
			return (
				this.customers.items?.length &&
				!this.loading &&
				!this.loadingEnvironments
			);
		},
		resultCounter() {
			return `${this.customers.items?.length} of ${this.customers.totalCount}`;
		},
		hasMoreItemsToLoad() {
			return this.page < this.customers.totalPageCount;
		},
		hasOpenAccordions() {
			return (
				this.customersAccordionStatuses.length > 0 ||
				this.companiesAccordionStatuses.length > 0
			);
		}
	},
	methods: {
		...mapActions('customers', [
			'loadCustomers',
			'loadCustomerAutosuggestions'
		]),
		...mapActions('options', ['setOptionsEnvironments']),
		collapseNode(node) {
			if (node.isOpenStatus) {
				node.collapse();
			}

			for (const children of node.$children) {
				if (children) {
					this.collapseNode(children);
				}
			}
		},
		collapseAllTiers() {
			this.$refs.customersList.$children.forEach(element => {
				this.collapseNode(element);
			});
			this.$store.commit('customers/clearAccordionStatuses');
		},
		async fetchData(page) {
			if (!this.loadingMore) {
				this.loading = true;
			}

			let params = _.cloneDeep(this.filters);

			params.page = page || this.page;
			params.count = 100;
			params.active = true;
			params.search = this.searchOptions.search;

			try {
				await this.loadCustomers(params);
			} finally {
				this.loading = false;
				this.loadingMore = false;
				this.$refs.searchWithAutosuggestRef.hide();
			}
		},
		async onSearchChange() {
			if (this.searchOptions.search && this.searchOptions.search.trim()) {
				this.$store.commit('customers/clearCustomersAutosuggestions');

				await this.loadCustomerAutosuggestions({
					search: this.searchOptions.search,
					searchType: this.searchOptions.searchType
				});
				this.createAutosuggestOptions();
				this.$refs.searchWithAutosuggestRef.visible();
			} else {
				this.$refs.searchWithAutosuggestRef.hide();
			}
		},
		onSearchOptionsSelected() {
			this.$store.commit('customers/clearCustomersAutosuggestions');
			this.$refs.searchWithAutosuggestRef.hide();
			this.filters.search = this.searchOptions.search;
			this.filters.searchType = this.searchOptions.searchType;
		},
		createAutosuggestOptions() {
			// parse autosuggest response
			const options = [];
			this.autosuggestOptions = [];

			if (
				this.searchOptions.searchType === 'all' ||
				this.searchOptions.searchType === 'customer'
			) {
				options.push({ groupName: 'Clients', items: [] });

				const index = options.findIndex(
					option => option.groupName === 'Clients'
				);

				options[
					index
				].totalCount = this.customersAutosuggestions.customers?.totalCount;

				this.customersAutosuggestions.customers?.items.forEach(
					customer => {
						options[index].items.push({
							text: `${customer.customerName} (#${customer.dealershipGroupNumber})`,
							textValue: customer.customerName,
							subText: '',
							value: customer.DealershipGuid
						});
					}
				);
			}
			if (
				this.searchOptions.searchType === 'all' ||
				this.searchOptions.searchType === 'company'
			) {
				options.push({ groupName: 'Companies', items: [] });

				const index = options.findIndex(
					option => option.groupName === 'Companies'
				);

				options[
					index
				].totalCount = this.customersAutosuggestions.companies?.totalCount;

				this.customersAutosuggestions.companies?.items.forEach(
					company => {
						options[index].items.push({
							text: `${company.companyName} (${company.companyNumber})`,
							textValue: company.companyName,
							subText: company.customerName,
							value: company.companyId
						});
					}
				);
			}
			if (
				this.searchOptions.searchType === 'all' ||
				this.searchOptions.searchType === 'location'
			) {
				options.push({ groupName: 'Sub Locations', items: [] });

				const index = options.findIndex(
					option => option.groupName === 'Sub Locations'
				);

				options[
					index
				].totalCount = this.customersAutosuggestions.locations?.totalCount;

				this.customersAutosuggestions.locations?.items.forEach(
					location => {
						options[index].items.push({
							text: location.locationName,
							textValue: location.locationName,
							subText: `${location.customerName} | Co.(${location.companyNumber})`,
							value: location.locationId
						});
					}
				);
			}

			this.autosuggestOptions = options;
		},
		updateRoute(queries) {
			let queryParameters = getDifferentValues(
				this.defaultFilters,
				queries
			);

			this.$router
				.push({
					path: '/clients',
					query: queryParameters
				})
				.catch(() => {});
		},
		handleScrollEnd() {
			if (this.hasMoreItemsToLoad) {
				this.loadingMore = true;
				this.page += 1;
			}
		},
		updateFavoriteOnVuex() {
			this.$store.commit(
				'customers/SetFavoriteToggle',
				this.currentFavorite
			);
		}
	},
	watch: {
		filters: {
			deep: true,
			handler(obj) {
				this.updateRoute(obj);
				if (this.page != 1) {
					this.page = 1;
				} else {
					this.fetchData(1);
				}
			}
		},
		page: {
			handler(page) {
				this.fetchData(page);
			}
		},

		'$route.query': {
			immediate: true,
			handler(query) {
				let newQuery = _.cloneDeep(query);
				if (
					newQuery.favoritesort === 'true' ||
					newQuery.favoritesort === 'false'
				) {
					newQuery.favoritesort = JSON.parse(newQuery.favoritesort);
				} else {
					Object.assign(newQuery, {
						favoritesort: this.favoriteToggle
					});
				}

				this.searchOptions.search =
					newQuery.search || this.defaultFilters.search;
				this.searchOptions.searchType =
					newQuery.searchType || this.defaultFilters.searchType;

				let newfilters = { ...this.defaultFilters, ...newQuery };

				if (!_.isEqual(newfilters, this.filters)) {
					this.filters = _.cloneDeep(newfilters);
				}

				if (query.search) {
					this.$nextTick(() => {
						if (this.$refs.searchWithAutosuggestRef) {
							this.$refs.searchWithAutosuggestRef.clearOn();
						}
					});
				}
			}
		},
		searchOptions: {
			deep: true,
			immediate: true,
			handler(obj) {
				const { search } = obj;
				if (search) {
					this.$store.commit('customers/SetSearch', search);
				}
			}
		}
	},
	beforeRouteLeave(to, from, next) {
		if (to.name === 'customer-forms') {
			to.meta.breadcrumb = [
				{ name: 'Clients', data: { path: from.fullPath } },
				{ name: 'Forms' }
			];
			const obj = { path: to.fullPath, breadcrumb: to.meta.breadcrumb };
			this.$store.commit('setBreadCrumbs', obj);
		}
		next();
	}
};
</script>

<style lang="scss" scoped>
/**
  * Customers Result Page
  * Can use SCSS variables = TRUE
**/
.results-toolbar__group {
	margin-left: auto;
}
.results-toolbar__collapse {
	margin-right: $gutter-md;
}
</style>
